import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { type HTMLAttributes } from "react";
import { twMerge } from "tailwind-merge";
import { useMediaQuery } from "../hooks/media-query";
import { Check, ChevronRight } from "../icons";

const DropdownMenu = DropdownMenuPrimitive.Root;

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

const DropdownMenuGroup = DropdownMenuPrimitive.Group;

const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

const DropdownMenuSub = DropdownMenuPrimitive.Sub;

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

const DropdownMenuSubTrigger = ({
  className,
  inset,
  children,
  ref,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubTrigger> & {
  inset?: boolean;
}) => (
  <DropdownMenuPrimitive.SubTrigger
    className={twMerge(
      "focus:bg-grey-50 data-[state=open]:bg-grey-50 flex cursor-default select-none items-center gap-2 rounded-md p-2.5 text-sm font-medium outline-hidden transition-colors data-disabled:pointer-events-none data-disabled:opacity-50",
      inset === true && "pl-8",
      className,
    )}
    {...props}
    ref={ref}
  >
    {children}
    <ChevronRight className="text-grey-400 ml-auto h-4 w-4" />
  </DropdownMenuPrimitive.SubTrigger>
);
DropdownMenuSubTrigger.displayName =
  DropdownMenuPrimitive.SubTrigger.displayName;

const DropdownMenuSubContent = ({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubContent>) => {
  const isDesktop = useMediaQuery("sm");
  return (
    <DropdownMenuPrimitive.SubContent
      className={twMerge(
        "border-grey-200 text-grey-700 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 min-w-32 overflow-hidden rounded-md border bg-white px-1.5 py-1 shadow-lg",
        className,
      )}
      sideOffset={isDesktop ? undefined : -193}
      alignOffset={isDesktop ? undefined : -5}
      {...props}
      ref={ref}
    />
  );
};
DropdownMenuSubContent.displayName =
  DropdownMenuPrimitive.SubContent.displayName;

const DropdownMenuContent = ({
  className,
  ref,
  sideOffset = 4,
  containerRef,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Content> & {
  containerRef?: HTMLElement | null;
}) => (
  <DropdownMenuPrimitive.Portal container={containerRef}>
    <DropdownMenuPrimitive.Content
      align="start"
      sideOffset={sideOffset}
      className={twMerge(
        "border-grey-200 text-grey-700 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 min-w-32 overflow-hidden rounded-lg border bg-white px-1.5 py-1 shadow-lg",
        className,
      )}
      {...props}
      ref={ref}
    />
  </DropdownMenuPrimitive.Portal>
);
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

const DropdownMenuItem = ({
  className,
  testId,
  inset,
  ref,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Item> & {
  inset?: boolean;
  testId?: string;
}) => (
  <DropdownMenuPrimitive.Item
    data-cy={testId}
    className={twMerge(
      "focus:bg-grey-50 focus:text-grey-900 relative flex cursor-default select-none items-center gap-2 rounded-md p-2.5 text-sm font-medium outline-hidden transition-colors data-disabled:pointer-events-none data-disabled:opacity-50",
      inset === true && "pl-8",
      className,
    )}
    {...props}
    ref={ref}
  />
);
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

const DropdownMenuCheckboxItem = ({
  className,
  children,
  checked,
  ref,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.CheckboxItem>) => (
  <DropdownMenuPrimitive.CheckboxItem
    className={twMerge(
      "focus:bg-grey-50 focus:text-grey-900 relative flex cursor-default select-none items-center justify-between rounded-md py-1.5 pl-8 pr-2 text-sm font-medium outline-hidden transition-colors data-disabled:pointer-events-none data-disabled:opacity-50",
      className,
    )}
    checked={checked}
    {...props}
    ref={ref}
  >
    {children}
    <DropdownMenuPrimitive.ItemIndicator>
      <Check className="stroke-primary-500 size-4" />
    </DropdownMenuPrimitive.ItemIndicator>
  </DropdownMenuPrimitive.CheckboxItem>
);
DropdownMenuCheckboxItem.displayName =
  DropdownMenuPrimitive.CheckboxItem.displayName;

const DropdownMenuRadioItem = ({
  className,
  children,
  ref,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.RadioItem>) => (
  <DropdownMenuPrimitive.RadioItem
    className={twMerge(
      "focus:bg-grey-50 focus:text-grey-900 relative flex cursor-default select-none items-center rounded-md py-1.5 pl-8 pr-2 text-sm font-medium outline-hidden transition-colors data-disabled:pointer-events-none data-disabled:opacity-50",
      className,
    )}
    {...props}
    ref={ref}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <div className="bg-primary-500 h-2 w-2" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.RadioItem>
);
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;

const DropdownMenuLabel = ({
  className,
  inset,
  ref,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Label> & {
  inset?: boolean;
}) => (
  <DropdownMenuPrimitive.Label
    className={twMerge(
      "text-grey-500 px-2 py-1.5 text-xs font-medium",
      inset === true && "pl-8",
      className,
    )}
    {...props}
    ref={ref}
  />
);
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

const DropdownMenuSeparator = ({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Separator>) => (
  <DropdownMenuPrimitive.Separator
    className={twMerge("bg-grey-200 -mx-1.5 my-1 h-px", className)}
    {...props}
    ref={ref}
  />
);
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

const DropdownMenuShortcut = ({
  className,
  ...props
}: HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={twMerge(
        "bg-grey-50 border-grey-100 text-grey-400 ml-auto flex items-center justify-center rounded-md border px-1 py-0.5 text-xs",
        className,
      )}
      {...props}
    />
  );
};
DropdownMenuShortcut.displayName = "DropdownMenuShortcut";

export {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
};
